import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core'
import {
  COLUMN_TYPE_OVERRIDE_ICON,
  COLUMN_TYPE_OVERRIDE_ICON_COLOR_CLASS,
  COLUMN_TYPE_OVERRIDE_OPTIONS,
  ColumnTypeOverride,
  ColumnTypeOverrideOption,
  DEFAULT_COLUMN_TYPE_OVERRIDE,
  isColumnTypeIconClass,
} from '../../models/column-type-override.model'

/**
 * The comparison-type picker for a column whose catalog type is a placeholder.
 *
 * Purely presentational — it neither reads nor writes
 * {@link ColumnTypeOverrideService}. Both surfaces that host it (the filter modal's editor row and
 * the grid header's custom filter) own the write, so the picker can be dropped into either without
 * one of them acquiring the other's persistence behaviour.
 *
 * Shown ONLY for AGGREGATE_INFO-storage columns. A COLUMN / POS_DATA column's catalog type is
 * trustworthy and must not be user-overridable — offering the control there would let a user break
 * a column that works.
 *
 * Rendered as a compact dropdown (current choice + chevron, expanding to a checklist) rather than a
 * button group, matching the Match Rules aggInfo picker and the design mockups — both surfaces are
 * inline next to other compact controls (a column select, an operator select), where a row of three
 * always-visible buttons doesn't fit the same visual language.
 */
@Component({
  selector: 'hs-column-type-picker',
  templateUrl: './column-type-picker.component.html',
  styleUrls: ['./column-type-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColumnTypePickerComponent {
  /** The currently-chosen type. Defaults to Text, matching the catalog's own placeholder. */
  @Input() type: ColumnTypeOverride = DEFAULT_COLUMN_TYPE_OVERRIDE

  /** Label rendered above the control; overridable so the grid-header popover can shorten it. */
  @Input() label = 'Compare as'

  @Output() typeChange = new EventEmitter<ColumnTypeOverride>()

  readonly options: readonly ColumnTypeOverrideOption[] =
    COLUMN_TYPE_OVERRIDE_OPTIONS

  iconFor(type: ColumnTypeOverride): string {
    return COLUMN_TYPE_OVERRIDE_ICON[type]
  }

  iconColorClassFor(type: ColumnTypeOverride): string {
    return COLUMN_TYPE_OVERRIDE_ICON_COLOR_CLASS[type]
  }

  /** Date's icon is an mdi icon-font class; Text/Number render as a literal glyph. */
  isIconClass(icon: string): boolean {
    return isColumnTypeIconClass(icon)
  }

  get currentOption(): ColumnTypeOverrideOption {
    return (
      this.options.find(option => option.value === this.type) ?? this.options[0]
    )
  }

  onPick(value: ColumnTypeOverride): void {
    if (value === this.type) return
    this.type = value
    this.typeChange.emit(value)
  }
}
